/**
 * Return-URL do login: guarda o destino pretendido quando o usuário cai em
 * /login deslogado, para levá-lo de volta após autenticar.
 *
 * O destino chega via `?redirect=` (setado pelo middleware/AuthGuard). Como o
 * login Google faz um round-trip para o Google e volta, o valor é guardado em
 * sessionStorage antes de iniciar o OAuth (sobrevive à ida/volta na mesma aba)
 * e lido no callback. Sempre validado como path interno para evitar open
 * redirect (só aceita `/rota`, nunca `//host` nem URL absoluta).
 */

const STORAGE_KEY = 'postLoginRedirect'
const BACKSLASH = 0x5c
const CONTROL_CHAR_MAX = 0x1f

/**
 * Aceita apenas paths internos absolutos (`/algo`), barrando `//` e URLs
 * externas. Também rejeita barra invertida e control chars: `/\evil.com`
 * começa com `/` e não com `//`, mas os browsers normalizam `\`→`/`,
 * resolvendo para `//evil.com` (host externo); control chars (< U+0020) podem
 * disparar normalização inesperada na resolução da URL.
 */
export function isSafeInternalPath(path: string | null | undefined): path is string {
	if (typeof path !== 'string') return false
	if (!path.startsWith('/') || path.startsWith('//')) return false
	for (let i = 0; i < path.length; i++) {
		const code = path.charCodeAt(i)
		if (code === BACKSLASH || code <= CONTROL_CHAR_MAX) return false
	}
	return true
}

/** Guarda o destino (se for path interno válido) para uso pós-login. */
export function stashPostLoginRedirect(path: string | null | undefined): void {
	if (typeof window === 'undefined' || !isSafeInternalPath(path)) return
	try {
		window.sessionStorage.setItem(STORAGE_KEY, path)
	} catch {
		// sessionStorage indisponível (modo restrito) — ignora; cai no fallback '/'.
	}
}

/**
 * Consome o destino guardado (removendo-o) e retorna um path interno seguro,
 * ou '/' como fallback. Prioriza `explicit` (ex.: `?redirect=` ainda na URL).
 */
export function takePostLoginRedirect(explicit?: string | null): string {
	let stored: string | null = null
	if (typeof window !== 'undefined') {
		try {
			stored = window.sessionStorage.getItem(STORAGE_KEY)
			window.sessionStorage.removeItem(STORAGE_KEY)
		} catch {
			stored = null
		}
	}
	if (isSafeInternalPath(explicit)) return explicit
	if (isSafeInternalPath(stored)) return stored
	return '/'
}
